import { includes } from 'lodash/fp';
import styled, { css, DefaultTheme } from 'styled-components';
import { MEDIA_QUERY } from '@optimus-web/breakpoints';
import { ANIMATION_CURVE, ANIMATION_DURATION, getSpacingToken, getToken, ZIndex } from '@optimus-web/common';
import {
    StyledTransitionGroup,
    StyledTransitionGroupProps,
    TransitionGroupChildBaseProps,
    TransitionState,
} from '@optimus-web/helpers';
import { Backdrop } from '../Backdrop';
import { ButtonElement } from '../Button';
import { Stack } from '../Stack';
import { CloseHandlerVariant } from './types';

export const getMinSwipeDistance = ({ theme }: { theme: DefaultTheme }) => getToken('sizing600')({ theme });

const getCloseHandlerJustifyContent = (closeHandler: CloseHandlerVariant) => {
    switch (closeHandler) {
        case 'close':
            return 'flex-end';
        case 'back':
            return 'flex-start';
        default:
            return 'center';
    }
};

const Transition = (props: Partial<StyledTransitionGroupProps<TransitionGroupChildBaseProps>>) => (
    <StyledTransitionGroup {...props} />
);

interface DrawerWrapperProps {
    zIndex?: number;
}

export const DrawerWrapper = styled(Transition)<DrawerWrapperProps>`
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${({ zIndex }) => zIndex || ZIndex.High};
    pointer-events: none;
    overflow: hidden;
    will-change: auto;
`;

export const DrawerBackdrop = styled(Backdrop)<TransitionGroupChildBaseProps>`
    opacity: ${getToken('opacity0')};
    transition: opacity ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};
    pointer-events: initial;

    ${(props) =>
        includes(props.transitionState, [TransitionState.ENTERING, TransitionState.EXITING]) &&
        css`
            opacity: ${getToken('opacity0')};
        `}
    ${(props) =>
        props.transitionState === TransitionState.ENTERED &&
        css`
            opacity: ${getToken('opacity1000')};
        `}
`;

interface DrawerContainerProps extends TransitionGroupChildBaseProps {
    open: boolean;
    height: `${number}%` | 'auto';
    maxHeight: `${number}%`;
    swipingInProgress: boolean;
}

export const DrawerContainer = styled.div<DrawerContainerProps>`
    background-color: ${getToken('backgroundStaticFloating')};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    ${(props) =>
        !props.swipingInProgress && `transition: transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};`}
    transform: translate3d(0, ${(props) => (props.open ? '0' : '100%')}, 0);
    height: ${(props) => props.height};
    max-height: ${(props) => props.maxHeight};
    width: 100%;
    pointer-events: initial;
    box-shadow: ${getToken('shadow300')};
    display: flex;
    flex-direction: column;
    border-radius: ${getToken('borderRadius300')}px ${getToken('borderRadius300')}px 0 0;
    padding-bottom: ${getSpacingToken('300')}px;
    overflow: hidden;
`;

export const DrawerCloseHandler = styled.div<{ closeHandler?: CloseHandlerVariant }>`
    background-color: ${getToken('backgroundStaticFloating')};
    height: ${getMinSwipeDistance}px;
    display: flex;
    justify-content: ${({ closeHandler }) => closeHandler && getCloseHandlerJustifyContent(closeHandler)};
    ${({ closeHandler }) => closeHandler === 'drag' && 'cursor: pointer;'}
    ${({ closeHandler }) =>
        closeHandler !== 'drag' &&
        css`
            padding: ${getSpacingToken('100')}px;
        `}
`;

export const DrawerDragBar = styled.div`
    width: ${getToken('sizing1200')}px;
    height: ${getToken('sizing50')}px;
    background-color: ${getToken('borderStaticPrimary')};
    border-radius: ${getToken('borderRadius100')}px;
    margin-top: ${getSpacingToken('150')}px;
`;

interface DrawerContentProps {
    closeHandler?: CloseHandlerVariant;
    withoutPadding?: boolean;
}

export const DrawerContent = styled.div<DrawerContentProps>`
    background-color: ${getToken('backgroundStaticFloating')};
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: ${({ closeHandler, theme }) => (closeHandler === 'none' ? `${getSpacingToken('300')({ theme })}px` : '0')}
        ${({ withoutPadding, theme }) => (withoutPadding ? '0' : `${getSpacingToken('200')({ theme })}px`)} 0;
`;

export const DrawerFooterElement = styled.footer`
    padding-top: ${getSpacingToken('300')}px;
`;

interface DrawerFooterBaseStackProps {
    responsive: boolean;
}

export const DrawerFooterBaseStack = styled(Stack).attrs({
    itemAlignment: 'center',
    spacing: '200',
})<DrawerFooterBaseStackProps>`
    ${ButtonElement} {
        ${MEDIA_QUERY.largePhoneMax} {
            width: 100%;
        }
    }
    ${({ responsive }) =>
        responsive &&
        css`
            ${MEDIA_QUERY.largePhoneMax} {
                width: 100%;
            }
        `};
`;

export const DrawerFooterPrimaryStack = styled(DrawerFooterBaseStack)<{ responsive: boolean }>`
    margin-left: auto;
    ${({ responsive }) =>
        responsive &&
        css`
            ${MEDIA_QUERY.largePhoneMax} {
                order: -1;
                flex-direction: column-reverse;
            }
        `}
`;
